import { useLingui } from "@lingui/react/macro";
import { Button, LinkButton } from "@offstreetparking/design-system/forms";
import { AddIcon } from "@offstreetparking/design-system/icons/AddIcon";
import { zodPlateValidation } from "@offstreetparking/domain/entities/vehicle";
import type { CountryValue } from "@offstreetparking/geography";
import { useFieldArray } from "react-hook-form";
import * as z from "zod/v4";
import type { RouterOutputs } from "../../../../api/types";
import { VehicleInput } from "../../../CreatePermitWizard/components/ParkingDetailsForm/VehicleInput";
import { Form } from "../../../forms/Form";
import { LoggedInVehicleSelector } from "./LoggedInVehicleSelector";

export type EditPermitVehiclesFormVehicle = {
	id?: number;
	plate: string;
	state: string;
};
type ParkerVehicle = RouterOutputs["parkers"]["getParkerVehicles"]["items"][number];
type EditPermitVehiclesFormProps = {
	vehicles: Pick<ParkerVehicle, "id" | "plate" | "state">[];
	country?: string;
	companyState?: string;
	maxVehicleCount?: number;
	isSubmitting: boolean;
	isParker: boolean;
	onSubmit: (vehicles: EditPermitVehiclesFormVehicle[]) => Promise<void>;
	onCreateVehicle?: () => void;
	onEditVehicle?: (vehicle: ParkerVehicle) => void;
};
export const EditPermitVehiclesForm = ({
	vehicles,
	country,
	companyState,
	maxVehicleCount,
	isSubmitting,
	isParker,
	onSubmit,
	onCreateVehicle,
	onEditVehicle,
}: EditPermitVehiclesFormProps) => {
	const { t } = useLingui();
	return (
		<Form
			schema={z.object({
				vehicles: z
					.array(
						z.object({
							id: z.number().optional(),
							plate: zodPlateValidation,
							state: z.string().min(1),
						}),
					)
					.min(1)
					.max(maxVehicleCount ?? 1),
			})}
			initialValues={{
				vehicles: vehicles.map((vehicle) => ({
					id: vehicle.id,
					plate: vehicle.plate,
					state: vehicle.state,
				})),
			}}
			onSubmit={async ({ vehicles: submittedVehicles }) => {
				if (!isParker) {
					await onSubmit(submittedVehicles);
					return;
				}
				const remapped: EditPermitVehiclesFormVehicle[] = submittedVehicles.map((submitted) => {
					const existing = vehicles.find((v) => v.plate === submitted.plate && v.state === submitted.state);
					return {
						id: existing?.id,
						plate: submitted.plate,
						state: submitted.state,
					};
				});
				await onSubmit(remapped);
			}}
			className="space-y-4"
		>
			{({ control, register, formState: { errors } }) => {
				const {
					fields,
					append: addVehicle,
					remove: removeVehicle,
				} = useFieldArray({
					control,
					name: "vehicles",
				});
				const canAddVehicle = !isParker && fields.length < (maxVehicleCount ?? 1);
				return (
					<>
						{isParker && (
							<div className="flex min-h-[60vh] flex-col gap-6 sm:min-h-0">
								<div className="flex-1">
									<LoggedInVehicleSelector
										isLoggedIn
										maxVehicles={maxVehicleCount ?? 1}
										initialVehicles={vehicles}
										onCreateVehicle={() => onCreateVehicle?.()}
										onEditVehicle={(vehicle) => onEditVehicle?.(vehicle)}
									/>
								</div>
								<div className="-mx-4 -mb-6 sticky bottom-0 border-neutral-faded border-t bg-white px-4 py-4 sm:relative sm:m-0 sm:border-0 sm:p-0">
									<Button type="submit" isLoading={isSubmitting} className="w-full">
										{t`Save`}
									</Button>
								</div>
							</div>
						)}
						{!isParker && (
							<>
								<div className="space-y-2">
									{fields.map((field, index) => (
										<VehicleInput
											key={field.id}
											index={index}
											register={register}
											errors={errors}
											country={country as CountryValue}
											showRemove={fields.length > 1}
											onRemove={() => removeVehicle(index)}
										/>
									))}
								</div>
								{canAddVehicle && (
									<LinkButton
										type="button"
										size="sm"
										onClick={() =>
											addVehicle({
												plate: "",
												state: companyState ?? "",
											})
										}
										disabled={isSubmitting}
									>
										<AddIcon className="mr-1 h-4 w-4" />
										{t`Add Vehicle`}
									</LinkButton>
								)}
								<div className="flex justify-end">
									<Button type="submit" isLoading={isSubmitting} className="w-full sm:w-auto">
										{t`Save`}
									</Button>
								</div>
							</>
						)}
					</>
				);
			}}
		</Form>
	);
};
